/**
 * 프리셋 검증(SR-MODE-03 규칙): **모형의 토크 한계로 오를 수 없는 경사는 배치하지 않는다.**
 *
 * 준정적 오르막 한계(바퀴 차량, 기어·마찰 포함) — 커널 PlayRig(cm · kg · s)의 모양만 본다(커널 의존 없음):
 *  - 토크 한계: sin θ ≤ F / (m g),  F = Σ(구동 포트) gearedTorque(정지 토크, gear) / 바퀴 반경 — 한 포트의 토크는 그 포트 바퀴들이 나눠 진다
 *    (gearedTorque = 정지 토크 ÷ |gear| — 커널 playrig.ts 의 단일 정의와 같은 식. 토크 공식 결정 전까지 유지 · DEV-001 §7.0)
 *  - 마찰 한계: tan θ ≤ μ · s,  s = 구동 축이 지는 무게 몫(정지 무게중심과 축 위치로 근사), μ = min(바퀴 μ, 표면 μ)
 *  - 판정은 여유 margin(기본 0.85)을 곱한 각으로 한다: 경사각 ≤ margin × min(토크 한계, 마찰 한계)
 * 오르막 = 코스 진행 방향(+z)으로 높아지는 경사(yaw 0). 내리막(yaw 180)은 이 규칙 대상이 아니다.
 */
import type { ModeDefinitionV1 } from "./types";

type V3 = readonly [number, number, number];
/** 커널 PlayRig 의 필요한 부분(구조적 타입) */
export interface RigLike {
  readonly chassis: {
    readonly center: V3;
    readonly mass: number;
    readonly casters?: readonly { readonly center: V3 }[];
  };
  readonly wheels: readonly {
    readonly center: V3;
    readonly radius: number;
    readonly mass: number;
    readonly port?: string | null;
    readonly gear?: number;
    readonly friction?: number;
  }[];
  readonly subBodies?: readonly { readonly mass: number }[];
  readonly motors: Readonly<Record<string, { readonly stallTorque: number }>>;
}

export interface ClimbLimit {
  /** 토크로 오를 수 있는 최대 경사(°) */
  readonly torqueDeg: number;
  /** 마찰 μ 에서 구동 바퀴가 미끄러지지 않는 최대 경사(°) */
  readonly frictionDeg: number;
  readonly limitDeg: number;
  readonly massKg: number;
  readonly driveForceN: number;
  readonly drivenShare: number;
}

const G = 981; // cm/s²
const DEG = 180 / Math.PI;

/** 한 모형의 오르막 한계(표면 μ 기본 = 바퀴 μ) */
export function climbLimit(rig: RigLike, surfaceFriction = Infinity): ClimbLimit {
  const mass =
    rig.chassis.mass +
    rig.wheels.reduce((s, w) => s + w.mass, 0) +
    (rig.subBodies ?? []).reduce((s, b) => s + b.mass, 0);
  // 포트별 토크를 그 포트 바퀴가 나눠 진다 → 포트마다 gearedTorque / 반경
  const byPort = new Map<string, { torque: number; radius: number }>();
  for (const w of rig.wheels) {
    if (!w.port) continue;
    const m = rig.motors[w.port];
    if (!m) continue;
    byPort.set(w.port, { torque: m.stallTorque / Math.abs(w.gear ?? 1), radius: w.radius });
  }
  let force = 0;
  for (const { torque, radius } of byPort.values()) force += torque / radius;
  const driven = rig.wheels.filter((w) => w.port && rig.motors[w.port]);
  const others = [
    ...rig.wheels.filter((w) => !(w.port && rig.motors[w.port])).map((w) => w.center[2]),
    ...(rig.chassis.casters ?? []).map((c) => c.center[2]),
  ];
  const zCom =
    (rig.chassis.center[2] * rig.chassis.mass +
      rig.wheels.reduce((s, w) => s + w.center[2] * w.mass, 0)) /
    (rig.chassis.mass + rig.wheels.reduce((s, w) => s + w.mass, 0));
  const zd = driven.reduce((s, w) => s + w.center[2], 0) / Math.max(1, driven.length);
  let share = 1;
  if (others.length && driven.length) {
    const zo = others.reduce((s, z) => s + z, 0) / others.length;
    share = Math.abs(zd - zo) < 1e-6 ? 0.5 : Math.min(1, Math.max(0, (zCom - zo) / (zd - zo)));
  }
  const mu = Math.min(surfaceFriction, ...driven.map((w) => w.friction ?? 0.9));
  const torqueDeg = Math.asin(Math.min(1, force / (mass * G))) * DEG;
  const frictionDeg = Math.atan(mu * share) * DEG;
  return {
    torqueDeg,
    frictionDeg,
    limitDeg: Math.min(torqueDeg, frictionDeg),
    massKg: mass,
    driveForceN: force / 100,
    drivenShare: share,
  };
}

export interface SlopeIssue {
  readonly preset: string;
  readonly ramp: string;
  readonly slug: string;
  readonly angleDeg: number;
  readonly limitDeg: number;
}

/** 모드 정의의 모든 프리셋 오르막이 모든 대표 모형의 한계 × margin 안인가 — 넘는 것 목록 */
export function validatePresetSlopes(
  def: ModeDefinitionV1,
  rigs: readonly { readonly slug: string; readonly rig: RigLike }[],
  margin = 0.85,
): SlopeIssue[] {
  const out: SlopeIssue[] = [];
  for (const p of def.presets)
    for (const e of [...def.env, ...p.env]) {
      if (e.prefab !== "ramp" || (e.yaw ?? 0) !== 0 || !e.size) continue;
      const angle = Math.atan2(e.size[1], e.size[2]) * DEG;
      for (const { slug, rig } of rigs) {
        const lim = climbLimit(rig, e.friction ?? Infinity);
        if (angle > margin * lim.limitDeg)
          out.push({ preset: p.key, ramp: e.id, slug, angleDeg: angle, limitDeg: lim.limitDeg });
      }
    }
  return out;
}
